/**
 * Work Proof Prompt Component
 * Compliance-safe prompt for employees to confirm current work
 * 
 * Features:
 * - Random/idle-triggered prompts
 * - Simple text response
 * - Optional order linking
 * - Single 5-minute snooze
 * - Full audit logging
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ClipboardCheck,
  Clock,
  Send,
  Timer,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const WorkProofPrompt = ({ onActivityLogged }) => {
  const [promptData, setPromptData] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [response, setResponse] = useState('');
  const [linkedOrderId, setLinkedOrderId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snoozing, setSnoozing] = useState(false);
  const [recentOrders, setRecentOrders] = useState([]);
  const [snoozeRemaining, setSnoozeRemaining] = useState(null);

  // Check for prompts periodically
  const checkForPrompt = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/session-health/check-prompt`);
      
      if (response.data.show_prompt && response.data.prompt) {
        setPromptData(response.data.prompt);
        setShowPrompt(true);
        
        // Check snooze status
        const maxSnoozes = 1; // From config
        const currentSnoozes = response.data.prompt.snooze_count || 0;
        setSnoozeRemaining(maxSnoozes - currentSnoozes);
      }
    } catch (err) {
      // Silently fail - don't interrupt user
      console.error('Error checking for prompt:', err);
    }
  }, []);

  // Fetch recent orders for linking
  const fetchRecentOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/orders?limit=10&sort=-updated_at`);
      setRecentOrders(response.data.orders || []);
    } catch (err) {
      console.error('Error fetching orders:', err);
    }
  }, []);

  useEffect(() => {
    // Initial check after 2 minutes (give user time to settle in after login)
    const initialTimeout = setTimeout(checkForPrompt, 120000);
    
    // Then check every 5 minutes (less aggressive polling)
    const interval = setInterval(checkForPrompt, 300000);
    
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [checkForPrompt]);

  useEffect(() => {
    if (showPrompt) {
      fetchRecentOrders();
    }
  }, [showPrompt, fetchRecentOrders]);

  const handleSubmit = async () => {
    if (!response.trim() || response.length < 5) {
      toast.error('Please provide a brief description of your current work');
      return;
    }

    try {
      setSubmitting(true);
      
      await axios.post(`${API}/api/session-health/respond-prompt`, {
        prompt_id: promptData.id,
        response_text: response.trim(),
        linked_order_id: linkedOrderId && linkedOrderId !== 'none' ? linkedOrderId : null
      });

      toast.success('Response recorded. Thank you!');
      setShowPrompt(false);
      setResponse('');
      setLinkedOrderId('');
      setPromptData(null);
      
      // Log activity
      if (onActivityLogged) {
        onActivityLogged('form_submit', 'work_proof', promptData.id);
      }
    } catch (err) {
      console.error('Error submitting response:', err);
      toast.error(err.response?.data?.detail || 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSnooze = async () => {
    if (snoozeRemaining <= 0) {
      toast.error('No snoozes remaining for this prompt');
      return;
    }

    try {
      setSnoozing(true);
      
      const result = await axios.post(`${API}/api/session-health/snooze-prompt`, {
        prompt_id: promptData.id
      });

      toast.info(`Snoozed for 5 minutes. ${result.data.snoozes_remaining} snooze(s) remaining.`);
      setShowPrompt(false);
      setSnoozeRemaining(result.data.snoozes_remaining);
      
    } catch (err) {
      console.error('Error snoozing prompt:', err);
      toast.error(err.response?.data?.detail || 'Failed to snooze');
    } finally {
      setSnoozing(false);
    }
  };

  const getTriggerReasonText = (reason) => {
    switch (reason) {
      case 'user_idle':
        return 'You appear to be idle';
      case 'user_inactive':
        return 'Extended period of inactivity detected';
      case 'random_check':
        return 'Routine work check-in';
      default:
        return 'Work check-in';
    }
  };

  if (!showPrompt || !promptData) {
    return null;
  }

  return (
    <Dialog open={showPrompt} onOpenChange={() => {}}>
      <DialogContent 
        className="max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <ClipboardCheck className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle className="text-lg">Work Check-In</DialogTitle>
              <DialogDescription className="text-sm">
                {getTriggerReasonText(promptData.trigger_reason)}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Transparency Notice */}
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-gray-500 mt-0.5" />
              <p className="text-xs text-gray-600">
                This is a routine check-in to help track work progress. Your response will be logged and visible to your team leader.
              </p>
            </div>
          </div>

          {/* Response Input */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              What are you currently working on? *
            </label>
            <Textarea
              placeholder="e.g., Reviewing tax documents for Order #12345, Preparing title search report..."
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              rows={3}
              className="resize-none"
              data-testid="work-proof-response"
              autoFocus
            />
            <p className="text-xs text-gray-400 mt-1">
              Minimum 5 characters required
            </p>
          </div>

          {/* Order Linking (Optional) */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Link to an order (optional)
            </label>
            <Select value={linkedOrderId} onValueChange={setLinkedOrderId}>
              <SelectTrigger>
                <SelectValue placeholder="Select an order..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No order selected</SelectItem>
                {recentOrders.map(order => (
                  <SelectItem key={order.id || order._id} value={order.id || order._id}>
                    {order.order_number || order.file_number} - {order.property_address?.substring(0, 30)}...
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Timestamp */}
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Clock className="w-3 h-3" />
            <span>
              {/* eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional browser-local time-only */}
              Prompted at {new Date(promptData.triggered_at).toLocaleTimeString()}
            </span>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {snoozeRemaining > 0 && (
            <Button
              type="button"
              variant="outline"
              onClick={handleSnooze}
              disabled={snoozing || submitting}
              className="gap-2"
            >
              {snoozing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Timer className="w-4 h-4" />
              )}
              Snooze 5 min
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            disabled={submitting || !response.trim() || response.length < 5}
            className="gap-2"
            data-testid="work-proof-submit"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkProofPrompt;
